import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Download, RefreshCw, CheckCircle, XCircle, ChevronRight, ChevronDown, FileText } from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import ReportViewerModal from '../components/ReportViewerModal';
import PdfViewerModal from '../components/PdfViewerModal';
import MvrPdfViewerModal from '../components/MvrPdfViewerModal';
import { useAuth } from '../context/AuthContext';
import {
  getReportsByUser, getReportsByType, getReportsByDay, getReportsByState,
  exportReportsByUserCsv, exportReportsByTypeCsv, exportReportsByDayCsv, exportReportsByStateCsv,
  ReportsFilters,
  ReportsByUserRow, ReportsByTypeRow, ReportsByDayRow, ReportsByStateRow,
  ReportsAggregateResponse,
} from '../services/reportsService';
import {
  getSearchHistory, exportSearchHistoryCsv, SearchHistoryEntry,
} from '../services/searchHistoryService';
import { getErrorMessage } from '../services/apiService';

type Pivot = 'user' | 'type' | 'day' | 'state' | 'rows';

const ROWS_PAGE_SIZE = 100;
const DRILL_PAGE_SIZE = 25;

const REQUEST_TYPES = ['NMVTIS', 'VIN Decode', 'NVS', 'MVR', 'ASTS Public'] as const;

const startOfMonthIso = (): string => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const todayIso = (): string => new Date().toISOString().split('T')[0];

const formatCurrency = (n: number): string => `$${n.toFixed(2)}`;

const pct = (n: number, total: number) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%';

// by-day buckets on the UTC calendar date; take it as-is so the drill asks for the same day.
const dayKey = (date: string): string => date.slice(0, 10);

// The "ASTS Integration" bucket has no user, so it drills with integrationOnly instead of a user id.
const userDrill = (r: ReportsByUserRow): Pick<ReportsFilters, 'filterUserId' | 'integrationOnly'> =>
  r.userId ? { filterUserId: r.userId } : { integrationOnly: true };

type ViewHandler = (entry: SearchHistoryEntry) => void;

const RecordInquiryLog: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [vin, setVin] = useState('');
  const [requestType, setRequestType] = useState<string>('');
  const [startDate, setStartDate] = useState(startOfMonthIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [orgWide, setOrgWide] = useState(false);

  const [pivot, setPivot] = useState<Pivot>('user');
  const [byUser, setByUser] = useState<ReportsAggregateResponse<ReportsByUserRow> | null>(null);
  const [byType, setByType] = useState<ReportsAggregateResponse<ReportsByTypeRow> | null>(null);
  const [byDay, setByDay] = useState<ReportsAggregateResponse<ReportsByDayRow> | null>(null);
  const [byState, setByState] = useState<ReportsAggregateResponse<ReportsByStateRow> | null>(null);
  const [rows, setRows] = useState<SearchHistoryEntry[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsLoadingMore, setRowsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filters as of the last Apply; drill-downs use these, not the half-edited inputs.
  const [appliedFilters, setAppliedFilters] = useState<ReportsFilters>({});
  const [loadSeq, setLoadSeq] = useState(0);

  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<{ logId: string; vin: string } | null>(null);
  const [mvrPdfData, setMvrPdfData] = useState<{ logId: string; vin: string } | null>(null);

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!isLoading && !user) navigate('/login');
  }, [user, isLoading, navigate]);

  const buildFilters = useCallback((): ReportsFilters => ({
    vin: vin.trim() || undefined,
    requestType: requestType || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    orgWide: orgWide || undefined,
  }), [vin, requestType, startDate, endDate, orgWide]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const filters = buildFilters();
    try {
      const [u, t, d, s, rowsResp] = await Promise.all([
        getReportsByUser(filters),
        getReportsByType(filters),
        getReportsByDay(filters),
        getReportsByState(filters),
        getSearchHistory(1, ROWS_PAGE_SIZE, filters.vin, filters.requestType, filters.startDate, filters.endDate, filters.orgWide),
      ]);
      setByUser(u); setByType(t); setByDay(d); setByState(s);
      setRows(rowsResp.searches);
      setRowsTotal(rowsResp.totalCount);
      setRowsPage(1);
      setAppliedFilters(filters);
      setLoadSeq(n => n + 1);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const loadMoreRows = async () => {
    setRowsLoadingMore(true);
    setError(null);
    const filters = buildFilters();
    try {
      const next = rowsPage + 1;
      const resp = await getSearchHistory(next, ROWS_PAGE_SIZE, filters.vin, filters.requestType, filters.startDate, filters.endDate, filters.orgWide);
      setRows(prev => [...prev, ...resp.searches]);
      setRowsTotal(resp.totalCount);
      setRowsPage(next);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setRowsLoadingMore(false);
    }
  };

  const handleView: ViewHandler = (entry) => {
    if (entry.status !== 'Success') return;
    if (entry.requestType === 'NMVTIS') setPdfData({ logId: entry.id, vin: entry.vin });
    else if (entry.requestType === 'MVR') setMvrPdfData({ logId: entry.id, vin: entry.vin });
    else setSelectedLogId(entry.id);
  };

  const exportCurrent = async () => {
    const filters = buildFilters();
    try {
      if (pivot === 'user') await exportReportsByUserCsv(filters);
      else if (pivot === 'type') await exportReportsByTypeCsv(filters);
      else if (pivot === 'day') await exportReportsByDayCsv(filters);
      else if (pivot === 'state') await exportReportsByStateCsv(filters);
      else await exportSearchHistoryCsv(filters.vin, filters.requestType, filters.startDate, filters.endDate, filters.orgWide);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  const currentTotalCount =
    pivot === 'user' ? byUser?.totalCount ?? 0 :
    pivot === 'type' ? byType?.totalCount ?? 0 :
    pivot === 'day' ? byDay?.totalCount ?? 0 :
    pivot === 'state' ? byState?.totalCount ?? 0 :
    rowsTotal;
  const currentTotalCost =
    pivot === 'user' ? byUser?.totalCost ?? 0 :
    pivot === 'type' ? byType?.totalCost ?? 0 :
    pivot === 'day' ? byDay?.totalCost ?? 0 :
    pivot === 'state' ? byState?.totalCost ?? 0 :
    rows.reduce((acc, r) => acc + r.cost, 0);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.04)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.03)_0%,transparent_70%)] rounded-full"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="relative z-10 flex-1 px-4 md:px-8 py-8 pt-28 md:pt-32 lg:ml-[220px] transition-all duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8" data-tour="reports">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-2">Reports</h1>
            <p className="text-[#64748b]">Counts and totals across your inquiry history. Click any row to see who ran them, down to the individual reports.</p>
          </div>

          {/* Filters */}
          <div className="glass-card-strong p-6 rounded-xl mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">VIN</label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value.toUpperCase())}
                  placeholder="Filter by VIN"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-black/10 bg-white text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Type</label>
                <select
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-black/10 bg-white text-sm"
                >
                  <option value="">All types</option>
                  {REQUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-black/10 bg-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-black/10 bg-white text-sm"
                />
              </div>
              <div className="flex items-end gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-[#0f172a]">
                  <input
                    type="checkbox"
                    checked={orgWide}
                    onChange={(e) => setOrgWide(e.target.checked)}
                    className="rounded border-black/20"
                  />
                  Organization-wide
                </label>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={loadAll}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Apply
              </button>
              <button
                onClick={exportCurrent}
                disabled={loading || currentTotalCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-black/10 bg-white text-sm font-semibold hover:bg-black/5 disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <div className="ml-auto text-sm text-[#64748b]">
                <span className="font-semibold text-[#0f172a]">{currentTotalCount.toLocaleString()}</span> reports
                {' · '}
                <span className="font-semibold text-[#0f172a]">{formatCurrency(currentTotalCost)}</span> total
              </div>
            </div>
          </div>

          {/* Pivot tabs */}
          <div className="glass-card-strong p-6 rounded-xl">
            <div className="flex flex-wrap gap-2 mb-4 border-b border-black/5">
              {([
                { key: 'user' as Pivot, label: 'By User' },
                { key: 'type' as Pivot, label: 'By Type' },
                { key: 'day' as Pivot, label: 'By Day' },
                { key: 'state' as Pivot, label: 'By State (MVR)' },
                { key: 'rows' as Pivot, label: 'View All' },
              ]).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setPivot(tab.key)}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
                    pivot === tab.key
                      ? 'text-[#c41e3a] border-b-2 border-[#c41e3a]'
                      : 'text-[#64748b] hover:text-[#0f172a]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
            )}

            {loading ? (
              <div className="py-12 text-center text-[#64748b]">
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#c41e3a]" />
                <p>Loading...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {pivot === 'user' && byUser && (
                  <DrillTable
                    key={`user-${loadSeq}`}
                    headers={['User', 'Email', 'Count', '% of Total', 'Total Cost']}
                    rows={byUser.items.map(r => ({
                      key: r.userId ?? 'integration',
                      cells: [r.username, r.email, r.count.toLocaleString(), pct(r.count, byUser.totalCount), formatCurrency(r.totalCost)],
                      detail: <TypesBreakdown filters={{ ...appliedFilters, ...userDrill(r) }} onView={handleView} />,
                    }))}
                  />
                )}
                {pivot === 'type' && byType && (
                  <DrillTable
                    key={`type-${loadSeq}`}
                    headers={['Type', 'Count', '% of Total', 'Total Cost']}
                    rows={byType.items.map(r => ({
                      key: r.type,
                      cells: [r.type, r.count.toLocaleString(), pct(r.count, byType.totalCount), formatCurrency(r.totalCost)],
                      detail: <UsersBreakdown filters={{ ...appliedFilters, requestType: r.type }} onView={handleView} />,
                    }))}
                  />
                )}
                {pivot === 'day' && byDay && (
                  <DrillTable
                    key={`day-${loadSeq}`}
                    headers={['Date', 'Count', '% of Total', 'Total Cost']}
                    rows={byDay.items.map(r => ({
                      key: dayKey(r.date),
                      cells: [dayKey(r.date), r.count.toLocaleString(), pct(r.count, byDay.totalCount), formatCurrency(r.totalCost)],
                      detail: <UsersBreakdown filters={{ ...appliedFilters, startDate: dayKey(r.date), endDate: dayKey(r.date) }} onView={handleView} />,
                    }))}
                  />
                )}
                {pivot === 'state' && byState && (
                  <DrillTable
                    key={`state-${loadSeq}`}
                    headers={['State', 'Count', '% of Total', 'Total Cost']}
                    rows={byState.items.map(r => ({
                      key: r.stateCode,
                      cells: [r.stateCode, r.count.toLocaleString(), pct(r.count, byState.totalCount), formatCurrency(r.totalCost)],
                      // by-state always counts MVR only, so the drill does too.
                      detail: <UsersBreakdown filters={{ ...appliedFilters, requestType: 'MVR', state: r.stateCode }} onView={handleView} />,
                    }))}
                  />
                )}
                {pivot === 'rows' && (
                  <RowsTable
                    rows={rows}
                    total={rowsTotal}
                    canLoadMore={rows.length < rowsTotal}
                    loadingMore={rowsLoadingMore}
                    onLoadMore={loadMoreRows}
                    onView={handleView}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {selectedLogId && (
        <ReportViewerModal
          isOpen={!!selectedLogId}
          onClose={() => setSelectedLogId(null)}
          logId={selectedLogId}
        />
      )}
      {pdfData && (
        <PdfViewerModal
          isOpen={!!pdfData}
          onClose={() => setPdfData(null)}
          logId={pdfData.logId}
          vin={pdfData.vin}
        />
      )}
      {mvrPdfData && (
        <MvrPdfViewerModal
          isOpen={!!mvrPdfData}
          onClose={() => setMvrPdfData(null)}
          logId={mvrPdfData.logId}
          vin={mvrPdfData.vin}
        />
      )}
    </div>
  );
};

interface DrillRow {
  key: string;
  cells: React.ReactNode[];
  detail: React.ReactNode;
}

// A table whose rows open to show `detail` underneath. Details mount on open, so each
// one fetches only when someone asks for it.
const DrillTable: React.FC<{ headers: string[]; rows: DrillRow[]; nested?: boolean }> = ({ headers, rows, nested }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  if (rows.length === 0) {
    return <div className="py-12 text-center text-[#64748b]">No reports match the current filters.</div>;
  }
  const toggle = (key: string) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-black/5">
          <th className="w-8" />
          {headers.map(h => (
            <th key={h} className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isOpen = open.has(row.key);
          return (
            <React.Fragment key={row.key}>
              <tr
                onClick={() => toggle(row.key)}
                className={`border-b border-black/5 cursor-pointer transition-colors ${isOpen ? 'bg-[#c41e3a]/[0.04]' : 'hover:bg-black/[0.02]'}`}
              >
                <td className="py-3 pl-2">
                  <button type="button" aria-expanded={isOpen} aria-label={isOpen ? 'Collapse' : 'Expand'} className="text-[#64748b]">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </td>
                {row.cells.map((cell, j) => (
                  <td key={j} className={`py-3 px-2 text-sm text-[#0f172a] ${j === 0 ? 'font-semibold' : ''}`}>{cell}</td>
                ))}
              </tr>
              {isOpen && (
                <tr className="border-b border-black/5">
                  <td colSpan={headers.length + 1} className={`py-3 pr-2 ${nested ? 'pl-6' : 'pl-8'}`}>
                    <div className="border-l-2 border-[#c41e3a]/30 pl-4">{row.detail}</div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// Fetches once per distinct filter set. Filters arrive as fresh objects every render,
// so the effect keys on their JSON instead of identity.
function useDrillFetch<T>(filters: ReportsFilters, fetcher: (f: ReportsFilters) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    fetcher(JSON.parse(filtersKey))
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(getErrorMessage(e)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);
  return { data, error };
}

const DrillStatus: React.FC<{ error: string | null }> = ({ error }) =>
  error
    ? <div className="py-3 text-sm text-red-700">{error}</div>
    : <div className="py-3 text-sm text-[#64748b] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#c41e3a]" /> Loading...</div>;

// Who ran the reports under a Type / Day / State row. When the type is already fixed
// (Type and State drills), a person opens straight to their reports; otherwise to their types.
const UsersBreakdown: React.FC<{ filters: ReportsFilters; onView: ViewHandler }> = ({ filters, onView }) => {
  const { data, error } = useDrillFetch(filters, getReportsByUser);
  if (!data) return <DrillStatus error={error} />;
  return (
    <DrillTable
      nested
      headers={['User', 'Email', 'Count', '% of Row', 'Total Cost']}
      rows={data.items.map(r => {
        const drilled = { ...filters, ...userDrill(r) };
        return {
          key: r.userId ?? 'integration',
          cells: [r.username, r.email, r.count.toLocaleString(), pct(r.count, data.totalCount), formatCurrency(r.totalCost)],
          detail: filters.requestType
            ? <ReportList filters={drilled} onView={onView} />
            : <TypesBreakdown filters={drilled} onView={onView} />,
        };
      })}
    />
  );
};

// One person's reports split by type, under a By User row.
const TypesBreakdown: React.FC<{ filters: ReportsFilters; onView: ViewHandler }> = ({ filters, onView }) => {
  const { data, error } = useDrillFetch(filters, getReportsByType);
  if (!data) return <DrillStatus error={error} />;
  return (
    <DrillTable
      nested
      headers={['Type', 'Count', '% of Row', 'Total Cost']}
      rows={data.items.map(r => ({
        key: r.type,
        cells: [r.type, r.count.toLocaleString(), pct(r.count, data.totalCount), formatCurrency(r.totalCost)],
        detail: <ReportList filters={{ ...filters, requestType: r.type }} onView={onView} />,
      }))}
    />
  );
};

// The individual reports at the bottom of a drill, paged.
const ReportList: React.FC<{ filters: ReportsFilters; onView: ViewHandler }> = ({ filters, onView }) => {
  const [rows, setRows] = useState<SearchHistoryEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filtersKey = JSON.stringify(filters);

  const fetchPage = useCallback((n: number) => {
    const f: ReportsFilters = JSON.parse(filtersKey);
    return getSearchHistory(n, DRILL_PAGE_SIZE, f.vin, f.requestType, f.startDate, f.endDate, f.orgWide, {
      filterUserId: f.filterUserId,
      integrationOnly: f.integrationOnly,
      state: f.state,
    });
  }, [filtersKey]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    fetchPage(1)
      .then(resp => {
        if (cancelled) return;
        setRows(resp.searches);
        setTotal(resp.totalCount);
        setPage(1);
      })
      .catch(e => { if (!cancelled) setError(getErrorMessage(e)); });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const next = page + 1;
      const resp = await fetchPage(next);
      setRows(prev => [...(prev ?? []), ...resp.searches]);
      setTotal(resp.totalCount);
      setPage(next);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  if (!rows) return <DrillStatus error={error} />;
  return (
    <>
      {error && <div className="py-2 text-sm text-red-700">{error}</div>}
      <RowsTable
        rows={rows}
        total={total}
        canLoadMore={rows.length < total}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        onView={onView}
      />
    </>
  );
};

interface RowsTableProps {
  rows: SearchHistoryEntry[];
  total: number;
  canLoadMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onView: ViewHandler;
}

const RowsTable: React.FC<RowsTableProps> = ({ rows, total, canLoadMore, loadingMore, onLoadMore, onView }) => {
  if (rows.length === 0) {
    return <div className="py-12 text-center text-[#64748b]">No reports match the current filters.</div>;
  }
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString();
  return (
    <>
      <table className="w-full">
        <thead>
          <tr className="border-b border-black/5">
            {['VIN', 'User', 'Type', 'Status', 'Date/Time', 'Cost', 'Report'].map(h => (
              <th key={h} className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-black/5 hover:bg-black/[0.02] transition-colors">
              <td className="py-3 px-2 text-sm font-mono text-[#0f172a]">{r.vin}</td>
              <td className="py-3 px-2 text-sm text-[#0f172a]">{r.username}</td>
              <td className="py-3 px-2 text-sm text-[#0f172a]">
                {r.requestType === 'NVS'
                  ? 'Theft/Lien'
                  : r.requestType === 'MVR' && r.state
                  ? `MVR · ${r.state}`
                  : r.requestType === 'ASTS Public' && r.state
                  ? `ASTS Public · ${r.state}`
                  : r.requestType}
              </td>
              <td className="py-3 px-2 text-sm">
                {r.status === 'Success' ? (
                  <span className="inline-flex items-center gap-1.5 text-[#16a34a] font-medium">
                    <CheckCircle className="w-4 h-4" /> Success
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[#dc2626] font-medium">
                    <XCircle className="w-4 h-4" /> Failed
                  </span>
                )}
              </td>
              <td className="py-3 px-2 text-sm text-[#475569]">{formatDateTime(r.searchedAt)}</td>
              <td className="py-3 px-2 text-sm text-[#0f172a]">${r.cost.toFixed(2)}</td>
              <td className="py-3 px-2 text-sm">
                {r.status === 'Success' ? (
                  <button
                    onClick={() => onView(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#fde2e8] text-[#c41e3a] text-xs font-semibold hover:bg-[#fbd0d9] transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    View
                  </button>
                ) : (
                  <span className="text-[#94a3b8]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex items-center justify-between text-sm text-[#64748b]">
        <span>Showing {rows.length.toLocaleString()} of {total.toLocaleString()}</span>
        {canLoadMore && (
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-black/10 bg-white font-semibold hover:bg-black/5 disabled:opacity-50"
          >
            {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
            Load more
          </button>
        )}
      </div>
    </>
  );
};

export default RecordInquiryLog;
